import React, { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Flex, Input, Space, Empty, Typography } from 'zui/Antd';
import { theme } from 'antd';

const { Text } = Typography;

const TableFilterDropdown = ({
	selectedKeys = [],
	setSelectedKeys,
	confirm,
	clearFilters,
	options, // [{label, value}] or { title, options: [{label, value}] }
	placeholder = 'Search'
}) => {
	const {
		token: {
			colorTextLabel,
			fontWeightStrong,
			paddingXS,
			padding,
			borderRadiusOuter,
			colorBorder,
			colorTextDescription
		}
	} = theme.useToken();

	const [searchText, setSearchText] = useState('');

	const isGrouped = Array.isArray(options) && options.some((o) => o && o.title && Array.isArray(o.options));

	const filtered = useMemo(() => {
		if (isGrouped) {
			return options
				.map((group) => ({
					...group,
					options: Array.isArray(group.options)
						? group.options.filter((opt) =>
								(opt.label || '').toLowerCase().includes(searchText.toLowerCase())
						  )
						: []
				}))
				.filter((group) => group.options.length > 0);
		} else {
			return options.filter((o) => (o.label || '').toLowerCase().includes(searchText.toLowerCase()));
		}
	}, [options, searchText, isGrouped]);

	// For grouped options, extract selected items and remove them from groups
	const { selectedItems, filteredGroups } = useMemo(() => {
		if (!isGrouped) return { selectedItems: [], filteredGroups: filtered };

		const allSelected = filtered.flatMap((group) =>
			group.options.filter((o) => selectedKeys.includes(o.value))
		);

		const groupsWithoutSelected = filtered.map((group) => ({
			...group,
			options: group.options.filter((o) => !selectedKeys.includes(o.value))
		})).filter((group) => group.options.length > 0);

		return { selectedItems: allSelected, filteredGroups: groupsWithoutSelected };
	}, [filtered, selectedKeys, isGrouped]);

	const selected = isGrouped
		? selectedItems
		: filtered.filter((o) => selectedKeys.includes(o.value));

	const notSelected = isGrouped
		? filtered.flatMap((group) => group.options.filter((o) => !selectedKeys.includes(o.value)))
		: filtered.filter((o) => !selectedKeys.includes(o.value));

	const list =
		selected.length > 0 ? [...selected, { value: '__divider__', isDivider: true }, ...notSelected] : notSelected;

	const toggle = (value) => {
		const next = selectedKeys.includes(value) ? selectedKeys.filter((v) => v !== value) : [...selectedKeys, value];
		setSelectedKeys(next);
		confirm?.({ closeDropdown: false });
	};

	const handleClear = () => {
		clearFilters?.();
		setSearchText('');
		confirm?.({ closeDropdown: false });
	};

	return (
		<Flex vertical style={{ borderRadius: borderRadiusOuter }}>
			<Flex justify='space-between' align='center' style={{ padding }}>
				<Input
					placeholder={placeholder}
					prefix={<SearchOutlined style={{ color: colorTextDescription }} />}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					variant='borderless'
				/>
				<Button type='text' onClick={handleClear} style={{ padding: 0, textDecoration: 'underline' }}>
					Clear
				</Button>
			</Flex>

			<Space
				direction='vertical'
				style={{ padding: `${paddingXS}px ${padding}px`, maxHeight: 280, overflow: 'auto' }}
			>
				{filtered.length === 0 ? (
					<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No results found' />
				) : isGrouped ? (
					<>
						{/* Show selected items first */}
						{selectedItems.length > 0 && (
							<>
								{selectedItems.map((opt) => (
									<Checkbox
										key={opt.value}
										checked={true}
										onChange={() => toggle(opt.value)}
									>
										{opt.label}
									</Checkbox>
								))}
								<Divider style={{ margin: 0, borderColor: colorBorder }} />
							</>
						)}

						{/* Show groups with unselected items */}
						{filteredGroups.map((group) => (
							<React.Fragment key={group.title || 'group-untitled'}>
								{group.title && (
									<Text
										style={{
											fontWeight: fontWeightStrong,
											color: colorTextLabel
										}}
									>
										{group.title}s
									</Text>
								)}
								{Array.isArray(group.options) &&
									group.options.map((opt) => (
										<Checkbox
											key={opt.value}
											checked={false}
											onChange={() => toggle(opt.value)}
										>
											{opt.label}
										</Checkbox>
									))}
							</React.Fragment>
						))}
					</>
				) : (
					list.map((item) =>
						item.isDivider ? (
							<Divider key='__divider__' style={{ margin: 0, borderColor: colorBorder }} />
						) : (
							<Checkbox
								key={item.value}
								checked={selectedKeys.includes(item.value)}
								onChange={() => toggle(item.value)}
							>
								{item.label}
							</Checkbox>
						)
					)
				)}
			</Space>
		</Flex>
	);
};

export default TableFilterDropdown;
